/**
 * PageLoader Component - Full Page Loading Spinner
 * 
 * Displays a centered loading spinner that takes up a significant portion of the page
 * Used to indicate that page content is loading or being processed
 * 
 * Features:
 * - Centered spinner using Bootstrap flexbox utilities
 * - Fixed height (30vh - 30% of viewport height)
 * - Primary color spinner matching Bootstrap theme
 * - Accessible with hidden loading text for screen readers
 * - Simple, lightweight component with no props or state
 * 
 * Styling:
 * - Container: Flexbox centered both horizontally and vertically
 * - Height: 30vh (30% of viewport height)
 * - Spinner: Bootstrap spinner-border with primary color
 * - Accessibility: Hidden text "Loading..." for screen readers
 * 
 * Dependencies:
 * - React: UI library
 * - Bootstrap CSS: Flexbox classes and spinner component
 * 
 * @component
 * @returns {JSX.Element} Centered loading spinner container
 * 
 * @example
 * // Conditional loading display
 * {isLoading ? <PageLoader /> : <MainContent />}
 * 
 * @example
 * // While fetching data
 * {data === null && <PageLoader />}
 * {data && <DataDisplay data={data} />}
 * 
 * @example
 * // In a page placeholder
 * <div>
 *   <Header />
 *   <PageLoader />
 *   <Footer />
 * </div>
 */

// PageLoader.js
import React from 'react';

/**
 * PageLoader Component
 * Displays a centered loading spinner occupying 30% of viewport height
 * 
 * @component
 * @returns {JSX.Element} Loading spinner container
 */
const PageLoader = () => {
  return (
    <div 
      className="d-flex justify-content-center align-items-center" 
      style={{ height: "30vh" }}
    >
      {/* 
        Bootstrap spinner-border component
        - Displays animated circular spinner
        - text-primary: Uses theme primary color
        - role="status": Indicates this is a status element
        - visually-hidden span: Provides text for screen readers
      */}
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );
};

export default PageLoader;

